import { CNPJ_LENGTH, CPF_LENGTH, cleanDocument } from "@/shared/lib/document";

export function pluralize(
  count: number,
  singular: string,
  plural: string,
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

/** Texto exibido quando a data é nula, vazia ou inválida. */
const INVALID_DATE_FALLBACK = "—";

export function formatDate(date: Date | string | null | undefined): string {
  if (date === null || date === undefined || date === "") {
    return INVALID_DATE_FALLBACK;
  }
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) {
    return INVALID_DATE_FALLBACK;
  }
  return new Intl.DateTimeFormat("pt-BR").format(dateObj);
}

/**
 * Formata um instante (ISO 8601 com timezone) para `dd/MM/yyyy HH:mm`
 * convertido para o fuso horário local do usuário. Use sempre que o
 * campo carregar hora relevante (ex: `expectedOutAt`, `rentedAt`,
 * `outAt`/`inAt`). Para campos que só têm data, prefira `formatDate`.
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (date === null || date === undefined || date === "") {
    return INVALID_DATE_FALLBACK;
  }
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) {
    return INVALID_DATE_FALLBACK;
  }
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(dateObj);
}

export function formatCPF(cpf: string): string {
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

export function formatCNPJ(cnpj: string): string {
  const cleaned = cleanDocument(cnpj);
  return cleaned.replace(
    /([0-9A-Z]{2})([0-9A-Z]{3})([0-9A-Z]{3})([0-9A-Z]{4})(\d{2})/,
    "$1.$2.$3/$4-$5",
  );
}

export function formatDocument(document: string): string {
  const cleaned = cleanDocument(document);
  if (cleaned.length === CNPJ_LENGTH) return formatCNPJ(cleaned);
  if (cleaned.length === CPF_LENGTH) return formatCPF(cleaned);
  return document;
}

export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
}

export function formatCEP(cep: string): string {
  const cleaned = cep.replace(/\D/g, "");
  return cleaned.replace(/(\d{5})(\d{3})/, "$1-$2");
}
